import React, { useState } from 'react';
import useDms from '../../hooks/useDms';
import { Loading, ErrorBox, Empty } from './shared';
import Pager from '../LiveIntel/Pager';
import { nfmt, money, compactUsd } from '../../lib/formatters';

const SORTS = [
  { value: '-revenue_usd', label: 'Revenue ↓' },
  { value: 'revenue_usd', label: 'Revenue ↑' },
  { value: '-tickets_sold', label: 'Tickets ↓' },
  { value: '-shows', label: 'Shows ↓' },
  { value: 'name', label: 'Name A–Z' },
];

const pct = (v, digits = 1) => (v == null ? '—' : `${(Number(v) * 100).toFixed(digits)}%`);

function ArtistProfile({ name, onBack }) {
  const { data, loading, error } = useDms(`artists/${encodeURIComponent(name)}`);

  if (loading) {
    return (
      <div className="lyve-intel">
        <Loading label="Loading artist profile…" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="lyve-intel">
        <ErrorBox error={error} />
      </div>
    );
  }

  const s = data?.summary || {};
  const f = data?.fee_signals || {};
  const tours = data?.tours_headlined || [];
  const engagements = data?.engagements || [];
  const boxscores = data?.boxscores_2026 || [];
  const recaps = data?.recaps || [];
  const yearEnd = data?.year_end_charts || [];
  const top2020s = data?.top_2020s_tours || [];

  const stats = [
    { label: 'Total revenue', value: compactUsd(s.revenue_usd) },
    { label: 'Tickets sold', value: nfmt(s.tickets_sold) },
    { label: 'Shows', value: nfmt(s.shows) },
    { label: 'Avg ticket price', value: money(s.avg_ticket_price_usd, '$') },
    { label: 'Avg sell-through', value: pct(s.avg_sell_through) },
    { label: 'Revenue / show', value: compactUsd(s.revenue_per_show_usd) },
  ];

  return (
    <div className="lyve-intel">
      <div className="page">
        <button className="rp-back" onClick={onBack}>
          ‹ Back to artists
        </button>

      <div className="rp-card" style={{ marginTop: 12, marginBottom: 16 }}>
        <h2 className="wide" style={{ fontSize: 24, margin: 0 }}>
          {data.name}
        </h2>
        <div className="muted" style={{ marginTop: 4 }}>
          {s.first_date && s.last_date ? `${s.first_date} → ${s.last_date}` : '—'}
        </div>
        <div className="rp-grid rp-cols-4" style={{ marginTop: 16 }}>
          {stats.map((k) => (
            <div className="rp-stat" key={k.label}>
              <div className="label">{k.label}</div>
              <div className="value" style={{ fontSize: 20 }}>
                {k.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rp-grid rp-cols-2">
        <div className="rp-card">
          <div className="section-title" style={{ marginTop: 0 }}>
            <h2>Fee signals</h2>
          </div>
          <div className="kv-list">
            <div className="kv">
              <span className="k">Total revenue</span>
              <span className="v">{compactUsd(f.total_revenue_usd)}</span>
            </div>
            <div className="kv">
              <span className="k">Total tickets</span>
              <span className="v">{nfmt(f.total_tickets_sold)}</span>
            </div>
            <div className="kv">
              <span className="k">Avg ticket price</span>
              <span className="v">{money(f.avg_ticket_price_usd, '$')}</span>
            </div>
            <div className="kv">
              <span className="k">Avg sell-through</span>
              <span className="v">{pct(f.avg_sell_through)}</span>
            </div>
            <div className="kv">
              <span className="k">Revenue / show</span>
              <span className="v">{compactUsd(f.revenue_per_show_usd)}</span>
            </div>
            <div className="kv">
              <span className="k">Shows</span>
              <span className="v">{nfmt(f.show_count)}</span>
            </div>
            <div className="kv">
              <span className="k">Engagements</span>
              <span className="v">{nfmt(f.engagement_count)}</span>
            </div>
            <div className="kv">
              <span className="k">2026 box scores</span>
              <span className="v">{f.has_2026_boxscores ? 'Yes' : '—'}</span>
            </div>
            <div className="kv">
              <span className="k">Career recap</span>
              <span className="v">{f.has_recap ? 'Yes' : '—'}</span>
            </div>
          </div>
        </div>

        <div className="rp-card">
          <div className="section-title" style={{ marginTop: 0 }}>
            <h2>Top 2020s tours</h2>
          </div>
          {top2020s.length === 0 && <div className="muted">Not in the top-2020s ranking.</div>}
          {top2020s.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Tour</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {top2020s.map((t, i) => (
                  <tr key={i}>
                    <td>{t.Rank}</td>
                    <td>{t.Tour}</td>
                    <td>{compactUsd(t['Revenue (USD, rounded)'])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {tours.length > 0 && (
        <section>
          <div className="section-title">
            <h2>Tours headlined ({tours.length})</h2>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Tour</th>
                <th>Status</th>
                <th>Revenue</th>
                <th>Tickets</th>
                <th>Shows reported</th>
              </tr>
            </thead>
            <tbody>
              {tours.map((t, i) => (
                <tr key={i}>
                  <td>{t.tour}</td>
                  <td>{t.status || '—'}</td>
                  <td>{compactUsd(t.revenue_usd)}</td>
                  <td>{nfmt(t.tickets_sold)}</td>
                  <td>{nfmt(t.shows_reported)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {boxscores.length > 0 && (
        <section>
          <div className="section-title">
            <h2>2026 box scores ({boxscores.length})</h2>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Venue</th>
                <th>City</th>
                <th>Revenue</th>
                <th>Tickets</th>
                <th>Capacity</th>
                <th>Sell-through</th>
              </tr>
            </thead>
            <tbody>
              {boxscores.map((b, i) => (
                <tr key={i}>
                  <td>{b.event_date}</td>
                  <td>{b.venue}</td>
                  <td>{b.city}</td>
                  <td>{compactUsd(b.revenue_usd)}</td>
                  <td>{nfmt(b.tickets_sold)}</td>
                  <td>{nfmt(b.capacity)}</td>
                  <td>{b.sell_through == null ? '—' : `${(Number(b.sell_through) * 100).toFixed(0)}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {engagements.length > 0 && (
        <section>
          <div className="section-title">
            <h2>Engagements ({engagements.length})</h2>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Venue</th>
                <th>City</th>
                <th>Country</th>
                <th>Revenue</th>
                <th>Tickets</th>
                <th>Sell-through</th>
              </tr>
            </thead>
            <tbody>
              {engagements.slice(0, 200).map((e, i) => (
                <tr key={i}>
                  <td>{e.date || '—'}</td>
                  <td>{e.venue}</td>
                  <td>{e.city || '—'}</td>
                  <td>{e.country || '—'}</td>
                  <td>{compactUsd(e.revenue_usd)}</td>
                  <td>{nfmt(e.tickets_sold)}</td>
                  <td>{e.sell_through == null ? '—' : `${(Number(e.sell_through) * 100).toFixed(0)}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {engagements.length > 200 && (
            <div className="muted" style={{ marginTop: 10 }}>
              Showing first 200 of {engagements.length}.
            </div>
          )}
        </section>
      )}

      {recaps.length > 0 && (
        <section>
          <div className="section-title">
            <h2>Career recap ({recaps.length})</h2>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Tour / show</th>
                <th>Billed as</th>
                <th>Role</th>
                <th>Revenue</th>
                <th>Tickets</th>
                <th>Shows</th>
              </tr>
            </thead>
            <tbody>
              {recaps.map((r, i) => (
                <tr key={i}>
                  <td>{r['Tour / show']}</td>
                  <td>{r['Billed as'] || '—'}</td>
                  <td>{r.Role || '—'}</td>
                  <td>{compactUsd(r['Revenue (USD)'])}</td>
                  <td>{nfmt(r['Tickets sold'])}</td>
                  <td>{nfmt(r.Shows)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {yearEnd.length > 0 && (
        <section>
          <div className="section-title">
            <h2>Year-end charts ({yearEnd.length})</h2>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Chart</th>
                <th>Rank</th>
                <th>Revenue</th>
                <th>Tickets</th>
              </tr>
            </thead>
            <tbody>
              {yearEnd.map((y, i) => (
                <tr key={i}>
                  <td>{y.Year}</td>
                  <td>{y.Chart}</td>
                  <td>{y.Rank}</td>
                  <td>{compactUsd(y['Revenue (USD)'])}</td>
                  <td>{nfmt(y['Tickets sold'])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      </div>
    </div>
  );
}

export default function ReportsArtists() {
  const [draft, setDraft] = useState('');
  const [applied, setApplied] = useState('');
  const [sort, setSort] = useState('-revenue_usd');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(40);
  const [selected, setSelected] = useState(null);

  const { data, loading, error } = useDms('artists', {
    q: applied,
    sort,
    limit: perPage,
    offset: (page - 1) * perPage,
  });

  const submit = (e) => {
    e.preventDefault();
    setApplied(draft);
    setPage(1);
  };

  if (selected) return <ArtistProfile name={selected.name} onBack={() => setSelected(null)} />;

  const total = data?.total ?? 0;

  return (
    <div className="lyve-intel">
      <div className="hero" style={{ paddingBottom: 0 }}>
        <div className="eyebrow">
          <i />
          REPORTS · ARTISTS
        </div>
        <h1 className="h-title wide">ARTISTS</h1>
        <p className="h-sub">
          Artists with touring sales aggregates, ranked by revenue, tickets or shows. Open one for its full sales profile
          and fee signals.
        </p>
      </div>

      <div className="page" style={{ paddingTop: 18 }}>
        <form className="acat-bar" onSubmit={submit}>
          <input
            className="acat-input"
            placeholder="Artist name"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <select
            className="acat-select"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              setPage(1);
            }}>
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <button className="acat-go" type="submit">
            Search
          </button>
        </form>

        {loading && <Loading />}
        {error && <ErrorBox error={error} />}
        {!loading && !error && data?.data?.length === 0 && <Empty label="No artists found." />}
        {!loading && !error && data?.data?.length > 0 && (
          <>
            <div className="fs-meta">
              <span className="num">{nfmt(total)}</span> <span className="faint">artists</span>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Artist</th>
                  <th>Revenue</th>
                  <th>Tickets</th>
                  <th>Shows</th>
                  <th>Tours</th>
                  <th>Avg price</th>
                  <th>Sell-through</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((a) => (
                  <tr key={a.name}>
                    <td className="clickable" onClick={() => setSelected(a)}>
                      {a.name}
                    </td>
                    <td>{compactUsd(a.revenue_usd)}</td>
                    <td>{nfmt(a.tickets_sold)}</td>
                    <td>{nfmt(a.shows)}</td>
                    <td>{nfmt(a.tours)}</td>
                    <td>{money(a.avg_ticket_price_usd, '$')}</td>
                    <td>{pct(a.avg_sell_through)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager
              total={total}
              page={page}
              perPage={perPage}
              onPage={setPage}
              onPerPage={(v) => {
                setPerPage(v);
                setPage(1);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
